import { createContext, useState, useContext, useEffect, useRef } from "react";
import axios from "axios";
import API_URL from '../config';

const API = API_URL;
const AuthContext = createContext();

// Décode le payload d'un JWT sans librairie externe
function decodeToken(token) {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch {
        return null;
    }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const refreshTimerRef = useRef(null);

    // Planifie le refresh 5 minutes avant l'expiration du token
    const scheduleRefresh = (currentToken) => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

        const payload = decodeToken(currentToken);
        if (!payload?.exp) return;

        const expiresAt = payload.exp * 1000;
        const delay = expiresAt - Date.now() - 5 * 60 * 1000; // 5 min avant expiry

        if (delay <= 0) {
            // Le token est déjà expiré ou expire dans moins de 5 min
            doRefresh(currentToken);
            return;
        }

        refreshTimerRef.current = setTimeout(() => doRefresh(currentToken), delay);
    };

    const doRefresh = async (currentToken) => {
        try {
            const res = await axios.post(
                `${API}/api/users/token/refresh`,
                {},
                { headers: { Authorization: `Bearer ${currentToken}` } }
            );
            const newToken = res.data.token;
            localStorage.setItem("token", newToken);
            setToken(newToken);
            scheduleRefresh(newToken);
        } catch (err) {
            console.warn("Refresh token échoué, déconnexion.", err);
            logout();
        }
    };

    // Au montage : restaure la session depuis localStorage
    useEffect(() => {
        const storedToken = localStorage.getItem("token");
        const storedUser  = localStorage.getItem("user");

        if (storedToken && storedUser) {
            const payload = decodeToken(storedToken);
            if (payload && payload.exp * 1000 > Date.now()) {
                setToken(storedToken);
                setUser(JSON.parse(storedUser));
                scheduleRefresh(storedToken);
            } else {
                // Token expiré au chargement : nettoyage immédiat
                localStorage.removeItem("token");
                localStorage.removeItem("user");
            }
        }

        return () => {
            if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        };
    }, []);

    const login = async (username, password) => {
        try {
            const response = await axios.post(
                `${API}/api/users/login`,
                { username, password },
                { headers: { "Content-Type": "application/json" } }
            );
            const { token: newToken, ...userData } = response.data;

            localStorage.setItem("user", JSON.stringify(userData));
            localStorage.setItem("token", newToken);

            setUser(userData);
            setToken(newToken);
            scheduleRefresh(newToken);

            return { success: true };
        } catch (err) {
            console.error("Erreur de connexion :", err.response?.data || err.message);
            return { success: false, error: err.response?.data?.message || "Erreur de connexion" };
        }
    };

    const logout = () => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        setUser(null);
        setToken(null);
    };

    return (
        <AuthContext.Provider value={{ user, token, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
